import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Film } from "lucide-react";

// Each frame is one beat of the "video". Total ≈ 60 seconds.
const FRAMES = [
  { src: "/onboarding/page-03.jpg", title: "Open TP Wallet → tap Create a New Wallet",            sub: "Set a password (≥ 8 chars, letters + numbers).", duration: 7 },
  { src: "/onboarding/page-04.jpg", title: "Name the wallet · agree to the terms",                sub: "Default name is fine. Tap Next.",                duration: 5 },
  { src: "/onboarding/page-05.jpg", title: "Tap Create Recovery Phrase",                          sub: "Tick the 3 acknowledgments → Copy and Continue.", duration: 8 },
  { src: "/onboarding/page-06.jpg", title: "Reveal the 12 words ✍️ — write them on paper",          sub: "Pen + paper only. NO screenshots. NO cloud notes.", duration: 22 },
  { src: "/onboarding/page-07.jpg", title: "Tap the 12 words back in the correct order",          sub: "Proves you actually wrote them down.",            duration: 10 },
  { src: "/onboarding/page-15.jpg", title: "✓ Done — your TP Wallet is live",                     sub: "You're back at the home screen with $0 balance.",  duration: 6 },
];
const TOTAL = FRAMES.reduce((s, f) => s + f.duration, 0);

function cumulativeStart(i) {
  let s = 0;
  for (let k = 0; k < i; k++) s += FRAMES[k].duration;
  return s;
}

export default function SeedBackupCoach({ compact = false }) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds
  const lastTick = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    if (!playing) {
      if (raf.current) cancelAnimationFrame(raf.current);
      lastTick.current = null;
      return;
    }
    const tick = (t) => {
      if (lastTick.current == null) lastTick.current = t;
      const dt = (t - lastTick.current) / 1000;
      lastTick.current = t;
      setElapsed((e) => {
        const n = e + dt;
        if (n >= TOTAL) { setPlaying(false); return TOTAL; }
        return n;
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [playing]);

  const currentIdx = (() => {
    let acc = 0;
    for (let i = 0; i < FRAMES.length; i++) {
      acc += FRAMES[i].duration;
      if (elapsed < acc) return i;
    }
    return FRAMES.length - 1;
  })();
  const current = FRAMES[currentIdx];

  const restart = () => { setElapsed(0); setPlaying(true); };
  const togglePlay = () => {
    if (elapsed >= TOTAL) restart();
    else setPlaying(!playing);
  };
  const jumpTo = (i) => { setElapsed(cumulativeStart(i)); };

  return (
    <div className={`glass overflow-hidden ${compact ? "" : "fade-up"}`} style={{ borderRadius: 22 }} data-testid="seed-coach">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)", color: "white" }}>
            <Film size={15} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-3">60-second walkthrough</div>
            <div className="font-display text-base font-semibold tracking-tight">What you'll see in TP Wallet</div>
          </div>
        </div>
        <div className="chip chip-blue text-[10px]" data-testid="coach-duration">{Math.ceil(TOTAL)}s</div>
      </div>

      {/* Viewport */}
      <div className="relative bg-black/90" style={{ aspectRatio: "16/9" }}>
        {FRAMES.map((f, i) => (
          <img
            key={f.src}
            src={f.src}
            alt={f.title}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
            style={{ opacity: i === currentIdx ? 1 : 0 }}
          />
        ))}
        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 px-5 py-4 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white" data-testid="coach-caption">
          <div className="text-[10px] uppercase tracking-wider font-mono text-white/65">Step {currentIdx + 1} of {FRAMES.length}</div>
          <div className="font-display text-base sm:text-lg font-semibold leading-tight mt-0.5">{current.title}</div>
          <div className="text-xs sm:text-sm text-white/80 mt-1">{current.sub}</div>
        </div>

        {/* Center play overlay when paused at start */}
        {!playing && elapsed === 0 && (
          <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center group" data-testid="coach-center-play">
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-white transition-all group-hover:scale-110" style={{ background: "rgba(64,96,255,0.92)", boxShadow: "0 20px 50px rgba(64,96,255,0.45)" }}>
              <Play size={32} fill="white" className="ml-1" />
            </div>
          </button>
        )}
      </div>

      {/* Scrubber + chapters */}
      <div className="px-4 py-3 bg-white/65">
        <div className="flex items-center gap-3">
          <button onClick={togglePlay} className="w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)" }} data-testid="coach-toggle-play">
            {playing ? <Pause size={14} /> : elapsed >= TOTAL ? <RotateCcw size={14} /> : <Play size={14} fill="white" className="ml-0.5" />}
          </button>
          <div className="flex-1">
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.08)" }} data-testid="coach-progress-bar">
              <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${(elapsed / TOTAL) * 100}%`, background: "linear-gradient(90deg, #4060FF, #6B8AFF)" }} />
            </div>
            <div className="flex justify-between mt-1 font-mono text-[10px] text-ink-3">
              <span data-testid="coach-elapsed">{Math.floor(elapsed)}s</span>
              <span>{Math.ceil(TOTAL)}s</span>
            </div>
          </div>
        </div>
        {/* Chapter chips */}
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="coach-chapters">
          {FRAMES.map((f, i) => (
            <button
              key={i}
              onClick={() => jumpTo(i)}
              className={`text-[10px] px-2 py-1 rounded-md font-medium transition-all ${i === currentIdx ? "text-white shadow-sm" : "text-ink-2 bg-white/55 hover:bg-white"}`}
              style={i === currentIdx ? { background: "linear-gradient(135deg, #4060FF, #6B8AFF)" } : { border: "1px solid rgba(15,18,38,0.08)" }}
              data-testid={`coach-chapter-${i}`}
            >
              {i + 1}. {f.title.split(" — ")[0].split("·")[0].trim().slice(0, 24)}{f.title.length > 24 ? "…" : ""}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
